import Layout from '@/components/feature/Layout';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaMarkup from '@/components/feature/SchemaMarkup';
import { siteInfo } from '@/mocks/siteInfo';

export default function AboutPage() {
  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    url: `${siteInfo.url}/about`,
  };

  return (
    <Layout>
      <SEOMeta
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解${siteInfo.name}的品牌故事、平台优势与联系方式。${siteInfo.tagline}`}
        keywords="关于禁漫天堂,禁漫天堂品牌,禁漫天堂联系方式"
        canonicalUrl={`${siteInfo.url}/about`}
      />
      <SchemaMarkup schema={orgSchema} />
      <SchemaMarkup schema={webPageSchema} />

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        <Breadcrumb items={breadcrumbItems} />

        <section className="mb-14">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
            关于禁漫天堂
          </h1>
          <div className="rounded-xl overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20digital%20content%20platform%20concept%2C%20abstract%20flowing%20shapes%20in%20warm%20coral%20and%20dark%20charcoal%20tones%2C%20elegant%20minimal%20aesthetic%2C%20soft%20cinematic%20lighting%2C%20smooth%20gradients%2C%20high%20detail%2C%20harmonious%20composition&width=1200&height=500&seq=about-hero&orientation=landscape"
              alt="关于禁漫天堂"
              className="w-full h-56 md:h-72 object-cover object-top"
            />
          </div>
          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
            <p className="text-foreground-600 leading-relaxed mb-4">
              <strong>禁漫天堂</strong>是一个专注于成人漫画内容聚合与阅读服务的平台，致力于为广大成熟用户提供丰富、优质的漫画阅读体验。平台汇集了来自日本、韩国、中国、欧美等多个国家和地区的海量成人漫画作品，涵盖多种题材与风格。
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4">
              自成立以来，禁漫天堂始终秉持"为用户提供便捷、安全的漫画阅读服务"的核心理念，不断优化平台功能，提升用户体验。无论是网页端还是移动端，用户都可以随时随地畅享精彩的漫画内容。
            </p>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
            品牌优势
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                <i className="ri-database-2-line text-lg text-primary-500"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">海量内容</h3>
              <p className="text-xs text-foreground-600 leading-relaxed">
                收录超过1,380部成人漫画作品，涵盖日漫、韩漫、国漫、欧美等多个品类，内容持续更新。
              </p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                <i className="ri-smartphone-line text-lg text-accent-500"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">多端适配</h3>
              <p className="text-xs text-foreground-600 leading-relaxed">
                支持网页端、Android和iOS移动端访问，提供专属APP下载，随时随地畅享阅读。
              </p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mb-3">
                <i className="ri-shield-check-line text-lg text-secondary-500"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">安全可靠</h3>
              <p className="text-xs text-foreground-600 leading-relaxed">
                采用业界标准加密技术保护用户数据隐私，确保用户信息安全，提供安心的阅读环境。
              </p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                <i className="ri-search-line text-lg text-primary-500"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">精准分类</h3>
              <p className="text-xs text-foreground-600 leading-relaxed">
                9大内容分类与智能搜索功能，帮助用户快速找到感兴趣的作品，提升浏览效率。
              </p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                <i className="ri-customer-service-2-line text-lg text-accent-500"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">优质服务</h3>
              <p className="text-xs text-foreground-600 leading-relaxed">
                提供VIP/SVIP会员服务，享有去广告、高清画质、离线下载等专属权益。
              </p>
            </div>

            <div className="p-5 rounded-xl bg-background-50 border border-background-200/70">
              <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mb-3">
                <i className="ri-refresh-line text-lg text-secondary-500"></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-950 mb-2">持续更新</h3>
              <p className="text-xs text-foreground-600 leading-relaxed">
                热门连载漫画定期更新，完结作品全集畅读，内容的时效性与完整性兼具。
              </p>
            </div>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm text-amber-400"></i>
                ))}
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                "禁漫天堂的漫画资源非常丰富，分类也很清晰，每天都能找到喜欢的作品。APP端阅读体验也很流畅。"
              </p>
              <div className="text-xs text-foreground-400">—— 资深用户 王先生</div>
            </div>

            <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm text-amber-400"></i>
                ))}
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                "用了很多漫画平台，禁漫天堂是内容最全、更新最快的。VIP会员价格也很实惠，性价比很高。"
              </p>
              <div className="text-xs text-foreground-400">—— VIP会员 李先生</div>
            </div>

            <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(4)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm text-amber-400"></i>
                ))}
                <i className="ri-star-line text-sm text-amber-400"></i>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                "韩漫专区的作品画风很棒，更新速度也不错。就是偶尔需要换入口稍微有点麻烦，还好有教程指导。"
              </p>
              <div className="text-xs text-foreground-400">—— 韩漫爱好者 张女士</div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-[var(--font-heading)] mb-6">
            联系我们
          </h2>
          <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-base font-semibold text-foreground-950 mb-4">联系方式</h3>
                <ul className="flex flex-col gap-3">
                  <li className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <span>{siteInfo.email}</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-accent-500"></i>
                    </div>
                    <span>jmtt.vip</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-foreground-600">
                    <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-secondary-500"></i>
                    </div>
                    <span>工作日 9:00 - 18:00</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-base font-semibold text-foreground-950 mb-4">快速反馈</h3>
                <p className="text-sm text-foreground-600 mb-4 leading-relaxed">
                  如果您有任何问题或建议，欢迎通过以下方式联系我们。我们的团队会在1-2个工作日内回复。
                </p>
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  <i className="ri-send-plane-line"></i>
                  发送邮件
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}